import { io, type Socket } from "socket.io-client";
import { API_ORIGIN, getToken } from "./api";
import { getCountry, type Country } from "./country";
import type { DomainExpiringEvent, LinkCheckFailedEvent } from "./types";

/**
 * Kết nối tới Socket.IO gateway của backend để nhận thông báo tức thời khi có
 * link check lỗi hoặc domain sắp hết hạn.
 *
 * Server bắt buộc JWT lúc handshake (`auth.token`). Hàm `auth` chạy lại mỗi lần
 * kết nối, kể cả reconnect, vì singleton này sống qua nhiều phiên đăng nhập.
 * Không có token thì không mở socket — server sẽ ngắt ngay và client khỏi vòng
 * reconnect vô ích.
 *
 * Superadmin gửi `query.country` để vào room đang xem. Tài khoản khác bị server
 * khoá vào quốc gia của chính họ, query chỉ là gợi ý.
 *
 * Dùng một singleton: mọi phần của app chia sẻ chung một kết nối, tránh mở nhiều
 * socket khi provider re-mount.
 */

export const SOCKET_EVENT_CHECK_FAILED = "link:check-failed";
export const SOCKET_EVENT_DOMAIN_EXPIRING = "domain:expiring";

/** Giữ nguyên alias cũ để tương thích nơi gọi. */
export const SOCKET_EVENT = SOCKET_EVENT_CHECK_FAILED;

let socket: Socket | null = null;

function applyCountry(s: Socket, country: Country = getCountry()) {
  s.io.opts.query = { country };
}

export function getSocket(): Socket {
  if (!socket) {
    socket = io(API_ORIGIN, {
      path: "/socket.io",
      // Cho phép fallback polling nếu websocket bị chặn ở tầng proxy.
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
      // Kết nối lười: chỉ mở khi có người subscribe và còn token.
      autoConnect: false,
      auth: cb => {
        cb({ token: getToken() ?? "" });
      },
      query: {
        country: getCountry(),
      },
    });
  }
  return socket;
}

/** Mở socket nếu còn access token. Gọi lại trước mỗi connect để room theo quốc gia hiện tại. */
function connectIfAuthed(s: Socket) {
  if (!getToken()) return;
  applyCountry(s);
  if (!s.connected) s.connect();
}

/**
 * Ngắt realtime khi đăng xuất hoặc 401. JWT chỉ được kiểm tra lúc handshake,
 * nên socket đang mở vẫn nhận sự kiện cho tới khi client tự đóng.
 */
export function disconnectSocket() {
  socket?.disconnect();
}

/** Chuyển room quốc gia của socket khi user đổi cờ. */
export function switchSocketCountry(country: Country) {
  if (!socket) return;
  applyCountry(socket, country);
  if (socket.connected) {
    socket.emit("join:country", country);
  }
}

function checkDisconnect() {
  if (!socket) return;
  if (
    socket.listeners(SOCKET_EVENT_CHECK_FAILED).length === 0 &&
    socket.listeners(SOCKET_EVENT_DOMAIN_EXPIRING).length === 0
  ) {
    socket.disconnect();
  }
}

/**
 * Đăng ký nghe sự kiện link:check-failed. Trả về hàm huỷ đăng ký; tự ngắt kết
 * nối khi không còn ai nghe nữa.
 */
export function onCheckFailed(handler: (ev: LinkCheckFailedEvent) => void): () => void {
  const s = getSocket();
  s.on(SOCKET_EVENT_CHECK_FAILED, handler);
  connectIfAuthed(s);

  return () => {
    s.off(SOCKET_EVENT_CHECK_FAILED, handler);
    checkDisconnect();
  };
}

/**
 * Đăng ký nghe sự kiện domain:expiring. Trả về hàm huỷ đăng ký.
 */
export function onDomainExpiring(handler: (ev: DomainExpiringEvent) => void): () => void {
  const s = getSocket();
  s.on(SOCKET_EVENT_DOMAIN_EXPIRING, handler);
  connectIfAuthed(s);

  return () => {
    s.off(SOCKET_EVENT_DOMAIN_EXPIRING, handler);
    checkDisconnect();
  };
}
